import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Calendar, Package, Truck, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCalendar = () => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchShipments();
  }, []);

  const fetchShipments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/warranty`, { withCredentials: true });
      // Filter warranties with tracking numbers
      const withTracking = response.data.filter(w => w.tracking_number || w.outbound_tracking);
      setShipments(withTracking);
    } catch (error) {
      console.error('Error fetching shipments:', error);
      toast.error('Error al cargar envíos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Solicitud Abierta': 'bg-blue-100 text-blue-800',
      'En Tránsito al Servicio Técnico': 'bg-amber-100 text-amber-800',
      'Entregado en Servicio Técnico': 'bg-purple-100 text-purple-800',
      'En Reparación': 'bg-orange-100 text-orange-800',
      'Reparado': 'bg-teal-100 text-teal-800',
      'Enviado al Cliente': 'bg-indigo-100 text-indigo-800',
      'RMA Completado': 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status.includes('Tránsito') || status.includes('Enviado')) return <Truck className="w-4 h-4" />;
    if (status.includes('Completado')) return <CheckCircle className="w-4 h-4" />;
    if (status.includes('Reparación') || status.includes('Reparado')) return <Clock className="w-4 h-4" />;
    return <Package className="w-4 h-4" />;
  };

  // Group shipments by date
  const groupedByDate = shipments.reduce((acc, shipment) => {
    const date = shipment.updated_at?.split('T')[0] || shipment.created_at?.split('T')[0] || 'Sin fecha';
    if (!acc[date]) acc[date] = [];
    acc[date].push(shipment);
    return acc;
  }, {});

  // Sort dates descending
  const sortedDates = Object.keys(groupedByDate).sort((a, b) => new Date(b) - new Date(a));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0047FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7] p-6" data-testid="admin-calendar">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <Link 
          to="/admin"
          className="inline-flex items-center gap-2 text-[#52525B] hover:text-[#0A0A0A] transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al Dashboard
        </Link>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#0A0A0A]">Calendario de Envíos</h1>
            <p className="text-[#52525B] mt-1">Gestiona y visualiza todos los envíos programados</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-xl px-4 py-2 border border-[#E5E5E5]">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#0047FF]" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="border-none focus:outline-none text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{shipments.length}</p>
                <p className="text-xs text-[#52525B]">Total Envíos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {shipments.filter(s => s.status?.includes('Tránsito')).length}
                </p>
                <p className="text-xs text-[#52525B]">En Tránsito</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {shipments.filter(s => s.status === 'RMA Completado').length}
                </p>
                <p className="text-xs text-[#52525B]">Completados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {shipments.filter(s => s.status?.includes('Reparación')).length}
                </p>
                <p className="text-xs text-[#52525B]">En Reparación</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timeline by Date */}
      <div className="max-w-7xl mx-auto">
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#0047FF]" />
              Historial de Envíos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sortedDates.length === 0 ? (
              <div className="text-center py-12 text-[#52525B]">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay envíos registrados</p>
              </div>
            ) : (
              <div className="space-y-6">
                {sortedDates.map((date) => (
                  <div key={date} className="relative">
                    {/* Date Header */}
                    <div className="sticky top-0 bg-white z-10 pb-2">
                      <div className="inline-flex items-center gap-2 bg-[#F5F5F7] px-3 py-1 rounded-full">
                        <Calendar className="w-4 h-4 text-[#0047FF]" />
                        <span className="text-sm font-medium">
                          {new Date(date).toLocaleDateString('es-ES', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </span>
                        <span className="text-xs bg-[#0047FF] text-white px-2 py-0.5 rounded-full">
                          {groupedByDate[date].length}
                        </span>
                      </div>
                    </div>
                    
                    {/* Shipments for this date */}
                    <div className="ml-4 border-l-2 border-[#E5E5E5] pl-4 space-y-3">
                      {groupedByDate[date].map((shipment) => (
                        <div 
                          key={shipment.request_id}
                          className="bg-[#F5F5F7] rounded-xl p-4 hover:bg-[#EBEBED] transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${getStatusColor(shipment.status)}`}>
                                {getStatusIcon(shipment.status)}
                              </div>
                              <div>
                                <p className="font-medium text-[#0A0A0A]">{shipment.product_name}</p>
                                <p className="text-sm text-[#52525B]">{shipment.full_name}</p>
                                <div className="flex items-center gap-4 mt-2 text-xs text-[#A1A1AA]">
                                  {shipment.tracking_number && (
                                    <span className="font-mono">IN: {shipment.tracking_number}</span>
                                  )}
                                  {shipment.outbound_tracking && (
                                    <span className="font-mono">OUT: {shipment.outbound_tracking}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(shipment.status)}`}>
                                {shipment.status}
                              </span>
                              <Link 
                                to={`/admin/warranty/${shipment.request_id}`}
                                className="block mt-2 text-xs text-[#0047FF] hover:underline"
                              >
                                Ver detalles
                              </Link>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminCalendar;
